import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useToast } from '@medely/ui-kit';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import { useProfessionalPoolContext } from '@mp/hooks/useProfessionalPoolContext';

const POOL_JOB_QUERY_KEYS = [
  'GetJobForJobDetailsContainer',
  'GetAvailableJobForAvailableJobDetailsContainer',
  'GetProfessionalPoolPqs',
  'GetProfessionalPoolOnboardingStatus',
  'GetMissingConditions',
  'job',
  'availableJob',
  'jobs',
  'professionalQualifications',
  'GetProfessionalTodos',
] as const;

const invalidatePoolJobQueries = (queryClient: QueryClient): void => {
  POOL_JOB_QUERY_KEYS.forEach((key) => {
    queryClient.invalidateQueries([key]);
  });
};

export type PoolMutationError = {
  message: string;
  status?: number;
};

const toPoolMutationError = (error: unknown): PoolMutationError => {
  const err = error as
    | {
        response?: { status?: number; data?: { error?: string; message?: string } };
        message?: string;
      }
    | undefined;
  return {
    message: err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? '',
    status: err?.response?.status,
  };
};

const JOIN_ERROR_FALLBACK = 'We could not add you to this pool. Please try again.';
const ACCEPT_ERROR_FALLBACK = 'We could not accept this pool invite. Please try again.';

export type UsePoolJoinMutationResult = {
  join: (variables: { jobId: number }) => Promise<unknown>;
  isJoining: boolean;
  error: PoolMutationError | null;
};

export const usePoolJoinMutation = (
  poolId: number | null | undefined,
): UsePoolJoinMutationResult => {
  const { axios } = useNetworkRequest();
  const queryClient = useQueryClient();
  const toast = useToast();

  const mutation = useMutation<unknown, unknown, { jobId: number }>(
    ({ jobId }) => axios.post(`/v3/professional/pools/${poolId}/join`, { job_id: jobId }),
    {
      onSuccess: () => {
        invalidatePoolJobQueries(queryClient);
      },
      onError: (error) => {
        toast.error(toPoolMutationError(error).message || JOIN_ERROR_FALLBACK);
      },
    },
  );

  return {
    join: mutation.mutateAsync,
    isJoining: mutation.isLoading,
    error: mutation.error ? toPoolMutationError(mutation.error) : null,
  };
};

export type UsePoolAcceptMutationResult = {
  accept: () => Promise<unknown>;
  isAccepting: boolean;
};

export const usePoolAcceptMutation = (
  poolId: number | null | undefined,
): UsePoolAcceptMutationResult => {
  const { axios } = useNetworkRequest();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { poolPqs } = useProfessionalPoolContext(poolId);
  const professionalQualificationId = poolPqs[0]?.id;

  const mutation = useMutation(
    () => {
      if (!professionalQualificationId) {
        return Promise.reject(new Error(ACCEPT_ERROR_FALLBACK));
      }
      return axios.patch(
        `/v3/professional/professional_pool_invites/${professionalQualificationId}`,
        { professional_qualification: { accepted: true } },
      );
    },
    {
      onSuccess: () => {
        invalidatePoolJobQueries(queryClient);
      },
      onError: (error) => {
        toast.error(toPoolMutationError(error).message || ACCEPT_ERROR_FALLBACK);
      },
    },
  );

  return {
    accept: mutation.mutateAsync,
    isAccepting: mutation.isLoading,
  };
};
